/**
 * CONTADORES DO MENU (auditoria de design de 11/10/2026, SEN-30; padrão de navegação A, item 3).
 *
 * Uma pílula ao lado de "Alertas", "Circuitos fora do ar", "Avisos emitidos", "Trocas de prefixo" e "DDoS inbound"
 * diz quanto espera por alguém SEM abrir a tela. (As duas últimas entraram por decisão do André em 12/10/2026.) A API calcula (`GET /api/admin/menu/contagens`); a web só desenha. Este arquivo é a definição única
 * dos três lados: quais contadores existem, quem os recebe, e a frase que o leitor de tela lê.
 *
 * Regras (cada uma tem um incidente por trás, ver `CLAUDE.md` §17.13):
 *  1. **O número é o total da lista que o item abre.** O item "Alertas" abre os alertas NÃO RESOLVIDOS; o contador
 *     é o `qtd_filtrado` dessa lista, com a mesma definição (não resolvido = sem verificação resolvida e sem
 *     incidente em quarentena ou descartado). Item cuja lista padrão não é o que se quer contar NÃO bate com o número
 *     (Trocas abre TODAS; DDoS abre o Painel, não a lista), que é exatamente o defeito que o DO-02 consertou nos
 *     cartões do Dashboard. A saída adotada em 12/10/2026: o contador traz o `destino` (endereço com o filtro) e,
 *     enquanto a pílula aparece, o item do menu leva para ESSA lista ("Trocas": `?prazo=proximos_7d`; "DDoS":
 *     `?aba=incidentes&status=em_andamento`). Sem pílula (zero ou fonte caída) o item volta ao endereço de sempre.
 *  2. **Fonte que não respondeu nunca vira zero.** Contador sem resposta chega como `null` e a casca não desenha
 *     pílula; zero também não desenha (pílula "0" é ruído de menu). Quem precisa saber se a fonte caiu abre a tela,
 *     que tem o aviso de erro. O menu nunca afirma "nada pendente" sem ter lido.
 *  3. **Quem não abre a tela não recebe o número** (o servidor só calcula os contadores do papel): contagem de
 *     alerta para o Financeiro seria vazar dado de uma tela que ele não vê.
 *  4. **Vermelho só quando é urgente, e a urgência é a que a PRÓPRIA TELA define.** `urgente > 0` pinta a pílula de
 *     vermelho: em "Circuitos fora do ar" é circuito que CAIU há mais de 5 min; em "DDoS inbound" é incidente "Em
 *     andamento" (a tela pinta esse cartão e essa etiqueta de vermelho). Alerta, aviso e troca de prefixo próxima são
 *     fila de trabalho, não incêndio: pílula neutra (a tela só pinta de vermelho o "Prazo vencido" de Trocas, que não
 *     está na lista "Próximos 7 dias").
 */
import { podeExecutar, ROLES, type Permissao, type Role } from "./permissoes.js";

export type IdContagemMenu = "alertas" | "circuitos_fora" | "avisos" | "trocas_proximas" | "ddos_andamento";

export interface DefinicaoContagemMenu {
  id: IdContagemMenu;
  /** `ItemNav.id` do registro de telas da web (`lib/menu.ts`). O teste de contrato confere que existe. */
  itemId: string;
  /** Permissão que a tela exige no servidor (a mesma do item no registro). */
  permissao: Permissao;
  /** Papéis que a permissão deixaria passar mas o MENU esconde (Circuitos fora do ar não é do Financeiro). */
  exceto?: readonly Role[];
  /**
   * Endereço (caminho e consulta) da lista com o filtro que o contador conta. Enquanto a pílula aparece, o item do menu
   * leva para cá, para o clique abrir exatamente a lista cujo total está na pílula. Os parâmetros precisam ser aceitos
   * pela configuração da lista (`lib/config-listas.ts` na web; o teste de contrato confere).
   */
  destino?: string;
}

export const CONTAGENS_MENU: readonly DefinicaoContagemMenu[] = [
  { id: "alertas", itemId: "alertas", permissao: "alertas.ver" },
  { id: "circuitos_fora", itemId: "circuitos-fora", permissao: "circuitos.ver", exceto: ["financeiro"] },
  { id: "avisos", itemId: "avisos", permissao: "cartas.ver" },
  // Decisão do André, 12/10/2026. Trocas: filtro "Próximos 7 dias" (a definição que a tela já usava). DDoS: Incidentes em andamento.
  { id: "trocas_proximas", itemId: "trocas", permissao: "trocas.ver", destino: "/admin/trocas-prefixo?prazo=proximos_7d" },
  { id: "ddos_andamento", itemId: "ddos", permissao: "alertas.ver", destino: "/admin/ddos-inbound?aba=incidentes&status=em_andamento" },
];

/** Os contadores que este papel recebe. */
export function contagensDoPapel(role: string): DefinicaoContagemMenu[] {
  if (!(ROLES as readonly string[]).includes(role)) return [];
  const papel = role as Role;
  return CONTAGENS_MENU.filter((d) => podeExecutar(papel, d.permissao) && !d.exceto?.includes(papel));
}

/** O que o servidor devolve para cada contador. `urgente` só existe onde há urgência (hoje, circuitos que caíram). */
export interface ContagemMenu {
  valor: number;
  urgente?: number;
}

export interface ContagemDescrita {
  valor: number;
  /** Texto que o leitor de tela lê junto do número ("4 alertas não resolvidos"). */
  rotulo: string;
  tom: "padrao" | "atraso";
}

const plural = (n: number, um: string, varios: string) => `${n.toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;

/** Frase + tom de um contador; `null` quando não há o que mostrar (zero, ausente ou valor inválido). */
export function descreverContagem(id: IdContagemMenu, c: ContagemMenu | null | undefined): ContagemDescrita | null {
  if (!c || !Number.isFinite(c.valor) || c.valor <= 0) return null;
  const valor = Math.floor(c.valor);
  const urgente = c.urgente && c.urgente > 0 ? Math.floor(c.urgente) : 0;
  switch (id) {
    case "alertas":
      return { valor, rotulo: plural(valor, "alerta não resolvido", "alertas não resolvidos"), tom: "padrao" };
    case "avisos":
      return { valor, rotulo: plural(valor, "aviso aguardando aprovação", "avisos aguardando aprovação"), tom: "padrao" };
    case "trocas_proximas":
      return { valor, rotulo: plural(valor, "troca de prefixo com prazo nos próximos 7 dias", "trocas de prefixo com prazo nos próximos 7 dias"), tom: "padrao" };
    case "ddos_andamento":
      return {
        valor,
        rotulo: plural(valor, "incidente de DDoS em andamento", "incidentes de DDoS em andamento"),
        // Vermelho: a tela define "Em andamento" como urgente. O servidor manda `urgente` = valor; sem ele, neutra.
        tom: urgente > 0 ? "atraso" : "padrao",
      };
    case "circuitos_fora": {
      const base = plural(valor, "circuito precisa de atenção", "circuitos precisam de atenção");
      return {
        valor,
        rotulo: urgente > 0 ? `${base}, ${urgente.toLocaleString("pt-BR")} fora do ar` : base,
        tom: urgente > 0 ? "atraso" : "padrao",
      };
    }
  }
}
